import { useEffect, useState } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import "../styles/home.css";
import "../styles/how-prufyy-works.css";
import { initScrollReveal } from "../utils/scrollReveal";
import { initScrollProgress } from "../utils/scrollProgress";

function HowPrufyyWorks() {
  const [mode, setMode] = useState("founder");

  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  useEffect(() => {
    initScrollReveal();
  }, [mode]);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              How Prufyy Works <br />
              From Narrative to Verifiable Clarity
            </h1>
            <p>
              Prufyy introduces structure between what founders say and how
              decision-makers verify it — without replacing human judgment.
            </p>
          </div>
        </section>

        {/* ================= TOGGLE ================= */}
        <div className="hpw-toggle reveal">
          <button
            className={mode === "founder" ? "active" : ""}
            onClick={() => setMode("founder")}
          >
            For Founders
          </button>
          <button
            className={mode === "evaluator" ? "active" : ""}
            onClick={() => setMode("evaluator")}
          >
            For Evaluators
          </button>
        </div>

        {/* ================= FOUNDER ================= */}
        {mode === "founder" && (
          <>
            <section className="section reveal">
              <span className="audience-badge">Founder Journey</span>
              <h2>Turn Your Pitch into Structured Credibility</h2>
              <p>
                Prufyy does not replace your pitch deck. It organizes the claims
                inside it so evaluators can verify what actually matters.
              </p>

              <div className="flow-diagram">
                <div className="flow-step">
                  <div className="flow-icon">📄</div>
                  <h3>Pitch Ingestion</h3>
                  <p>Your existing deck becomes structured input.</p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">🧩</div>
                  <h3>Claims Separated</h3>
                  <p>Market, traction, tech, and team claims are isolated.</p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">📎</div>
                  <h3>Attach Evidence</h3>
                  <p>Proof is attached only where credibility matters.</p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">🔗</div>
                  <h3>Share Once</h3>
                  <p>One profile shared across all evaluations.</p>
                </div>
              </div>
            </section>

            <section className="section reveal">
              <h2>Claims Are Anchored to Proof</h2>

              <div className="claim-evidence">
                <div className="claim-box">
                  <strong>Claim</strong>
                  <p>“We have strong enterprise traction.”</p>
                </div>

                <div className="link-line" />

                <div className="evidence-box">
                  <strong>Evidence</strong>
                  <ul>
                    <li>Signed LOIs</li>
                    <li>Revenue statements</li>
                    <li>Customer references</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="section reveal">
              <h2>Before vs After Prufyy</h2>

              <div className="before-after-static">
                <div className="before-card">
                  <h3>Before</h3>
                  <ul>
                    <li>Deck-only storytelling</li>
                    <li>Scattered documents</li>
                    <li>Repeated diligence questions</li>
                  </ul>
                </div>

                <div className="after-card">
                  <h3>After</h3>
                  <ul>
                    <li>Claims clearly isolated</li>
                    <li>Evidence attached per claim</li>
                    <li>Single structured profile</li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}

        {/* ================= EVALUATOR ================= */}
        {mode === "evaluator" && (
          <>
            {/* EVALUATOR INTRO */}
            <section className="section reveal">
              <span className="audience-badge">Evaluator Workflow</span>
              <h2>Evaluate Startups with Clarity, Not Persuasion</h2>
              <p>
                Prufyy is designed for angels, VCs, accelerators, and incubators
                who want to evaluate startups based on substance — not pitch
                performance, storytelling skill, or repetition.
              </p>
            </section>

            {/* EVALUATOR FLOW */}
            <section className="section reveal">
              <h2>A Structured Evaluation Flow</h2>

              <div className="flow-diagram">
                <div className="flow-step">
                  <div className="flow-icon">📥</div>
                  <h3>Intentional Access</h3>
                  <p>
                    Startups appear only when founders explicitly share them
                    with you.
                  </p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">🔍</div>
                  <h3>Claim-Level Review</h3>
                  <p>
                    Each claim is reviewed independently, without pitch noise.
                  </p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">📊</div>
                  <h3>Evidence in Context</h3>
                  <p>
                    Documents and links appear exactly where the claim is made.
                  </p>
                </div>
                <div className="flow-step">
                  <div className="flow-icon">✅</div>
                  <h3>Submit When Ready</h3>
                  <p>
                    Draft reviews are private. Submission locks accountability.
                  </p>
                </div>
              </div>
            </section>

            {/* DRAFT VS SUBMIT */}
            <section className="section reveal">
              <h2>Draft First, Submit with Intent</h2>
              <p>
                Prufyy separates thinking from signaling. You can explore,
                question, and reflect privately before submitting a final
                evaluation.
              </p>

              <div className="before-after-static">
                <div className="before-card">
                  <h3>Draft State</h3>
                  <ul>
                    <li>Private to you</li>
                    <li>Editable</li>
                    <li>No founder visibility</li>
                  </ul>
                </div>

                <div className="after-card">
                  <h3>Submitted Review</h3>
                  <ul>
                    <li>Locked permanently</li>
                    <li>Visible to founder</li>
                    <li>Creates accountability</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* EVALUATOR BENEFITS */}
            <section className="section reveal">
              <h2>Why Institutions Use Prufyy</h2>
              <ul>
                <li>Compare startups consistently across time and cohorts</li>
                <li>Reduce diligence fatigue and repetitive questioning</li>
                <li>Maintain evaluation history and institutional memory</li>
                <li>Support human judgment without forced scoring models</li>
              </ul>
            </section>
          </>
        )}

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Credibility Deserves Structure</h2>
          <p>
            Prufyy helps founders and evaluators focus on substance — not
            persuasion theatrics.
          </p>
          <a href="/signup" className="btn-primary">
            Get Started for Free
          </a>
        </section>

        {/* ================= SEO ================= */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How Prufyy Works",
            step: [
              "Extract startup claims",
              "Attach supporting evidence",
              "Share structured profiles",
              "Evaluate with human accountability",
            ],
          })}
        </script>
      </main>

      <Footer />
    </>
  );
}

export default HowPrufyyWorks;
